import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, withModifiers as _withModifiers, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "dropdown-content-inner" }
const _hoisted_2 = {
  key: 0,
  class: "dropdown-bottom-sheet-header"
}
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = ["onMousedown"]

import {
	ref,
	computed,
	watch,
	onMounted,
	onUnmounted,
	nextTick,
	provide,
	CSSProperties
} from 'vue'
import { useRoute } from 'vue-router'
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

type ResizeState = {
	startX: number
	startY: number
	lastX: number
	lastY: number
	direction: string
}

type Props = {
	position?: 'left' | 'right' | 'bottom'
	initialWidth?: number
	initialHeight?: number
	minWidth?: number
	minHeight?: number
	maxWidth?: number
	maxHeight?: number
	contentZIndex?: number
	resizable?: boolean
	closeByClickOutside?: boolean
	closeByEsc?: boolean
	hideByParentScroll?: boolean
	// CSS-селектор элементов вне дропдауна, клик по которым НЕ закрывает его
	ignoreClickOutside?: string
	name?: string
	trigger?: 'click' | 'hover'
	hoverDelay?: number
	hoverCloseDelay?: number
	fullscreenOnMobile?: boolean
	centeredOnMobile?: boolean
	bottomSheetOnMobile?: boolean
	mobileTitle?: string
	contentClass?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppResizableDropdown',
  props: {
    position: { default: 'bottom' },
    initialWidth: {},
    initialHeight: {},
    minWidth: { default: 300 },
    minHeight: { default: 300 },
    maxWidth: { default: 600 },
    maxHeight: { default: 600 },
    contentZIndex: {},
    resizable: { type: Boolean, default: false },
    closeByClickOutside: { type: Boolean, default: true },
    closeByEsc: { type: Boolean, default: true },
    hideByParentScroll: { type: Boolean, default: true },
    ignoreClickOutside: {},
    name: {},
    trigger: { default: 'click' },
    hoverDelay: { default: 600 },
    hoverCloseDelay: { default: 50 },
    fullscreenOnMobile: { type: Boolean, default: false },
    centeredOnMobile: { type: Boolean, default: false },
    bottomSheetOnMobile: { type: Boolean, default: true },
    mobileTitle: { default: '' },
    contentClass: { default: '' }
  },
  emits: ["open", "close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const isOpen = ref(false)
const wrapperRef = ref<HTMLElement>()
const contentRef = ref<HTMLElement>()
const contentWidth = ref(props.initialWidth ?? 0)
const contentHeight = ref(props.initialHeight ?? 0)
const isResizing = ref(false)
const isFullscreen = ref(false)
const isCenteredOnMobile = ref(false)
const isBottomSheetOnMobile = ref(false)
const bottomSheetMinHeight = ref(0)

const isAutoSize = computed(() => !props.resizable && props.initialWidth == null && props.initialHeight == null)

const backdropStyles = computed<CSSProperties>(() => ({
	zIndex: (props.contentZIndex ?? 10001) - 1
}))

const autoSizeRecalcTrigger = ref(0)

let openTimer: ReturnType<typeof setTimeout> | null = null
let closeTimer: ReturnType<typeof setTimeout> | null = null

// Флаг для отслеживания нахождения курсора над дропдауном
const isHoveringDropdown = ref(false)

// Отслеживание скролла родителя
let lastPosition = ref<{ top: number; left: number } | null>(null)

let scrollCleanups: (() => void)[] = []
let bottomSheetResizeObserver: ResizeObserver | null = null

let resizeState: ResizeState | null = null

const route = useRoute()

const canUseHover = computed(() =>
	props.trigger === 'hover' && !props.resizable && !isBottomSheetOnMobile.value
)

const computedPosition = computed(() => {
	if (isFullscreen.value || isBottomSheetOnMobile.value) return 'bottom'
	return calculateOptimalPosition()
})

const contentStyles = computed<CSSProperties>(() => {
	if (isFullscreen.value) {
		return {
			width: '100vw',
			height: '100dvh',
			zIndex: props.contentZIndex ?? 10001
		}
	}

	if (isBottomSheetOnMobile.value) {
		return {
			left: '0',
			right: '0',
			top: 'auto',
			bottom: '0',
			width: '100vw',
			height: 'auto',
			minHeight: bottomSheetMinHeight.value
				? `${bottomSheetMinHeight.value}px`
				: undefined,
			maxWidth: '100vw',
			maxHeight: 'calc(100dvh - 44px)',
			transform: 'none',
			zIndex: props.contentZIndex ?? 10001
		}
	}

	const styles: CSSProperties = {}

	if (props.contentZIndex != null) {
		styles.zIndex = props.contentZIndex
	}

	// Подписываемся на триггер для пересчёта позиции в авто-режиме
	// eslint-disable-next-line no-unused-expressions
	autoSizeRecalcTrigger.value

	if (isAutoSize.value) {
		styles.width = 'auto'
		styles.height = 'auto'
		styles.maxWidth = `${props.maxWidth}px`
		styles.maxHeight = `${props.maxHeight}px`
	} else {
		styles.width = `${contentWidth.value}px`
		styles.height = `${contentHeight.value}px`
	}

	if (isCenteredOnMobile.value) {
		styles.top = '50%'
		styles.left = '50%'
		styles.right = 'auto'
		styles.bottom = 'auto'
		styles.transform = 'translate(-50%, -50%)'
		styles.maxWidth = 'calc(100vw - 32px)'
		styles.maxHeight = 'calc(100dvh - 32px)'
		return styles
	}

	if (!isOpen.value || !wrapperRef.value) return styles

	const rect = wrapperRef.value.getBoundingClientRect()
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const gap = 5
	const minDistance = 20

	const elWidth = isAutoSize.value
		? (contentRef.value?.offsetWidth ?? 0)
		: contentWidth.value
	const elHeight = isAutoSize.value
		? (contentRef.value?.offsetHeight ?? 0)
		: contentHeight.value

	switch (computedPosition.value) {
		case 'bottom': {
			let top = rect.bottom + gap
			let left = rect.left + rect.width / 2 - elWidth / 2

			// Проверяем, помещается ли снизу
			if (top + elHeight + minDistance > viewportHeight) {
				// Помещаем сверху
				top = rect.top - gap - elHeight
			}

			// Корректируем по горизонтали
			if (left < minDistance) left = minDistance
			if (left + elWidth + minDistance > viewportWidth) {
				left = viewportWidth - elWidth - minDistance
			}

			styles.top = `${top}px`
			styles.left = `${left}px`
			break
		}

		case 'left': {
			let top = rect.top
			let left = rect.left - gap - elWidth

			// Корректируем по вертикали
			if (top < minDistance) top = minDistance
			if (top + elHeight + minDistance > viewportHeight) {
				top = viewportHeight - elHeight - minDistance
			}

			styles.top = `${top}px`
			styles.left = `${left}px`
			break
		}

		case 'right': {
			let top = rect.top
			let left = rect.right + gap

			// Корректируем по вертикали
			if (top < minDistance) top = minDistance
			if (top + elHeight + minDistance > viewportHeight) {
				top = viewportHeight - elHeight - minDistance
			}

			styles.top = `${top}px`
			styles.left = `${left}px`
			break
		}
	}

	return styles
})

const resizeCorners = computed(() => {
	const corners = []

	switch (computedPosition.value) {
		case 'left':
			corners.push(
				{ position: 'top-left', direction: 'top-left' },
				{ position: 'bottom-left', direction: 'bottom-left' }
			)
			break
		case 'right':
			corners.push(
				{ position: 'top-right', direction: 'top-right' },
				{ position: 'bottom-right', direction: 'bottom-right' }
			)
			break
		case 'bottom':
			corners.push(
				{ position: 'bottom-left', direction: 'bottom-left' },
				{ position: 'bottom-right', direction: 'bottom-right' }
			)
			break
	}

	return corners
})

// Метод для получения угла поворота иконки в зависимости от позиции
const getRotationAngle = (position: string): number => {
	switch (position) {
		case 'top-right':    // Правый верхний угол
			return 0
		case 'bottom-right': // Правый нижний угол
			return 90     // Поворот на 90°
		case 'bottom-left':  // Левый нижний угол
			return 180    // Поворот на 180°
		case 'top-left':     // Левый верхний угол
			return 270    // Поворот на 270°
		default:
			return 0
	}
}

const clearTimers = () => {
	if (openTimer) {
		clearTimeout(openTimer)
		openTimer = null
	}
	if (closeTimer) {
		clearTimeout(closeTimer)
		closeTimer = null
	}
}

const handleMouseEnter = () => {
	if (!canUseHover.value || isResizing.value) return
	clearTimers()

	openTimer = setTimeout(() => {
		if (!isOpen.value) {
			openDropdown()
		}
	}, props.hoverDelay)
}

const handleMouseLeave = () => {
	if (!canUseHover.value || isResizing.value) return

	// Если курсор не над контентом дропдауна, начинаем отсчет до закрытия
	if (!isHoveringDropdown.value) {
		scheduleClose()
	}
}

const handleActivatorMouseEnter = () => {
	if (!canUseHover.value) return
	handleMouseEnter()
}

const handleActivatorMouseLeave = () => {
	if (!canUseHover.value) return
	handleMouseLeave()
}

const handleContentMouseEnter = () => {
	if (!canUseHover.value) return
	isHoveringDropdown.value = true
	clearTimers()
}

const handleContentMouseLeave = () => {
	if (!canUseHover.value) return
	isHoveringDropdown.value = false
	scheduleClose()
}

const scheduleClose = () => {
	if (!canUseHover.value || isResizing.value) return

	clearTimers()
	closeTimer = setTimeout(() => {
		if (isOpen.value && !isHoveringDropdown.value) {
			closeDropdown()
		}
	}, props.hoverCloseDelay)
}


const handleActivatorClick = () => {
	if (canUseHover.value) {
		if (isOpen.value) {
			closeDropdown()
		} else {
			openDropdown()
		}
	} else {
		toggleDropdown()
	}
}

const handleActivatorClickCapture = (event: MouseEvent) => {
	if (!isBottomSheetOnMobile.value) return

	event.preventDefault()
	event.stopPropagation()
	handleActivatorClick()
}

const calculateOptimalPosition = () => {
	if (!wrapperRef.value || !isOpen.value) return props.position

	const rect = wrapperRef.value.getBoundingClientRect()
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20
	const gap = 5

	const elWidth = isAutoSize.value
		? (contentRef.value?.offsetWidth ?? 0)
		: contentWidth.value
	const elHeight = isAutoSize.value
		? (contentRef.value?.offsetHeight ?? 0)
		: contentHeight.value

	switch (props.position) {
		case 'left': {
			const leftSpace = rect.left - elWidth - gap
			if (leftSpace >= minDistance) return 'left'
			return 'right'
		}

		case 'right': {
			const rightSpace = viewportWidth - rect.right - elWidth - gap
			if (rightSpace >= minDistance) return 'right'
			return 'left'
		}

		case 'bottom': {
			const bottomSpace = viewportHeight - rect.bottom - elHeight - gap
			if (bottomSpace >= minDistance) return 'bottom'
			// Если снизу не хватает места, показываем сверху
			return 'bottom' // Будет откорректировано в contentStyles
		}

		default:
			return props.position
	}
}

// Получаем максимально доступные размеры для текущей позиции
const getMaxAvailableSize = () => {
	const rect = wrapperRef.value?.getBoundingClientRect()
	if (!rect) return { maxWidth: props.maxWidth, maxHeight: props.maxHeight }

	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20

	let maxWidth = props.maxWidth
	let maxHeight = props.maxHeight

	switch (computedPosition.value) {
		case 'left':
			// Максимальная ширина - до левого края экрана с отступом
			maxWidth = Math.min(maxWidth, rect.left - minDistance - 10)
			// Максимальная высота - высота viewport с отступами
			maxHeight = Math.min(maxHeight, viewportHeight - minDistance * 2)
			break

		case 'right':
			// Максимальная ширина - до правого края экрана с отступом
			maxWidth = Math.min(maxWidth, viewportWidth - rect.right - minDistance - 10)
			// Максимальная высота - высота viewport с отступами
			maxHeight = Math.min(maxHeight, viewportHeight - minDistance * 2)
			break

		case 'bottom': {
			// Максимальная высота - доступное пространство сверху или снизу
			const availableBottomSpace = viewportHeight - rect.bottom - minDistance - 10
			const availableTopSpace = rect.top - minDistance - 10
			maxHeight = Math.min(maxHeight, Math.max(availableBottomSpace, availableTopSpace))
			// Максимальная ширина - ширина viewport с отступами
			maxWidth = Math.min(maxWidth, viewportWidth - minDistance * 2)
			break
		}
	}

	return { maxWidth, maxHeight }
}

// Проверка размеров из LS на соответствие текущему viewport
const validateSavedSize = (savedWidth: number, savedHeight: number) => {
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20
	const maxAvailableWidth = viewportWidth - minDistance * 2
	const maxAvailableHeight = viewportHeight - minDistance * 2

	// Ограничиваем сохраненные размеры максимально доступными
	const validWidth = Math.min(savedWidth, maxAvailableWidth)
	const validHeight = Math.min(savedHeight, maxAvailableHeight)

	// Проверяем минимальные размеры
	return {
		width: Math.max(props.minWidth, validWidth),
		height: Math.max(props.minHeight, validHeight)
	}
}

const toggleDropdown = () => {
	if (isOpen.value) {
		closeDropdown()
	} else {
		openDropdown()
	}
}

const stopBottomSheetHeightTracking = () => {
	bottomSheetResizeObserver?.disconnect()
	bottomSheetResizeObserver = null
}

const rememberBottomSheetHeight = () => {
	if (!isBottomSheetOnMobile.value || !contentRef.value) return

	const availableHeight = Math.max(0, window.innerHeight - 44)
	const currentHeight = Math.min(
		Math.max(
			Math.ceil(contentRef.value.getBoundingClientRect().height),
			props.initialHeight ?? 0
		),
		availableHeight
	)

	if (currentHeight > bottomSheetMinHeight.value) {
		bottomSheetMinHeight.value = currentHeight
	}
}

const startBottomSheetHeightTracking = () => {
	stopBottomSheetHeightTracking()
	if (!isBottomSheetOnMobile.value || !contentRef.value) return

	rememberBottomSheetHeight()
	bottomSheetResizeObserver = new ResizeObserver(rememberBottomSheetHeight)
	bottomSheetResizeObserver.observe(contentRef.value)
}

const openDropdown = async () => {
	if (isOpen.value) return
	bottomSheetMinHeight.value = 0

	if (!isAutoSize.value) {
		if (props.name) {
			const savedSize = loadSizeFromStorage()
			if (savedSize) {
				const validatedSize = validateSavedSize(savedSize.width, savedSize.height)
				contentWidth.value = validatedSize.width
				contentHeight.value = validatedSize.height
			} else {
				contentWidth.value = props.initialWidth ?? props.minWidth
				contentHeight.value = props.initialHeight ?? props.minHeight
			}
		} else {
			contentWidth.value = props.initialWidth ?? props.minWidth
			contentHeight.value = props.initialHeight ?? props.minHeight
		}
	}

	isOpen.value = true
	isHoveringDropdown.value = false
	emit('open')

	await nextTick()
	updateFullscreenState()
	await nextTick()
	startBottomSheetHeightTracking()

	if (isAutoSize.value) {
		await nextTick()
		autoSizeRecalcTrigger.value++
	}

	if (!canUseHover.value) {
		document.addEventListener('mousedown', handleClickOutside)
	}
}

const closeDropdown = () => {
	isOpen.value = false
	isHoveringDropdown.value = false
	stopBottomSheetHeightTracking()
	bottomSheetMinHeight.value = 0
	clearTimers()

	if (!canUseHover.value) {
		document.removeEventListener('mousedown', handleClickOutside)
	}
	emit('close')
}

provide('appResizableDropdownClose', closeDropdown)

const handleBackdropClick = () => {
	if (isBottomSheetOnMobile.value) {
		closeDropdown()
	}
}

const handleClickOutside = (event: MouseEvent) => {
	if (!props.closeByClickOutside || isResizing.value || canUseHover.value) return
	const target = event.target as HTMLElement | null
	if (props.ignoreClickOutside && target?.closest(props.ignoreClickOutside)) {
		return
	}
	if (!contentRef.value?.contains(event.target as Node) &&
		!wrapperRef.value?.contains(event.target as Node)) {
		closeDropdown()
	}
}

const handleEsc = (event: KeyboardEvent) => {
	if (event.key !== 'Escape') {
		return
	}
	if (event.defaultPrevented) {
		return
	}
	if (!isOpen.value) {
		return
	}
	closeDropdown()
}

const startResize = (e: MouseEvent, direction: string) => {
	e.preventDefault()
	e.stopPropagation()

	isResizing.value = true

	resizeState = {
		startX: e.clientX,
		startY: e.clientY,
		lastX: e.clientX,
		lastY: e.clientY,
		direction
	}

	document.addEventListener('mousemove', handleResize)
	document.addEventListener('mouseup', stopResize)
}

const handleResize = (e: MouseEvent) => {
	if (!resizeState || !contentRef.value || !wrapperRef.value) return

	const deltaX = e.clientX - resizeState.lastX
	const deltaY = e.clientY - resizeState.lastY

	// Обновляем последнюю позицию
	resizeState.lastX = e.clientX
	resizeState.lastY = e.clientY

	// Используем текущие размеры как основу
	let newWidth = contentWidth.value
	let newHeight = contentHeight.value

	// Получаем текущие координаты
	const rect = wrapperRef.value.getBoundingClientRect()
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20
	const gap = 5

	// Вычисляем текущие границы контентного блока
	let currentTop = 0
	let currentBottom = 0
	let currentLeft = 0
	let currentRight = 0

	switch (computedPosition.value) {
		case 'bottom': {
			const top = parseFloat(contentStyles.value.top?.toString() || '0')
			const left = parseFloat(contentStyles.value.left?.toString() || '0')
			currentTop = top
			currentBottom = top + newHeight
			currentLeft = left
			currentRight = left + newWidth
			break
		}
		case 'left': {
			currentTop = rect.top
			currentBottom = rect.top + newHeight
			currentRight = rect.left - gap
			currentLeft = currentRight - newWidth
			break
		}
		case 'right': {
			currentTop = rect.top
			currentBottom = rect.top + newHeight
			currentLeft = rect.right + gap
			currentRight = currentLeft + newWidth
			break
		}
	}

	switch (resizeState.direction) {
		case 'top-left':
		case 'top-right':
			if (computedPosition.value === 'left' || computedPosition.value === 'right') {
				if (deltaY < 0) {
					// Увеличение высоты - поднимаем верхнюю границу
					const wouldBeTop = currentTop - Math.abs(deltaY)
					if (wouldBeTop >= minDistance) {
						newHeight += Math.abs(deltaY)
					}
				} else if (deltaY > 0) {
					// Уменьшение высоты - опускаем верхнюю границу
					// Проверяем, что не достигли нижней границы
					const wouldBeBottom = currentBottom - Math.abs(deltaY)
					if (wouldBeBottom - minDistance >= currentTop) { // Минимальная высота
						newHeight -= Math.abs(deltaY)
					}
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaY < 0) {
					// Уменьшение высоты - проверяем верхнюю границу
					const wouldBeTop = currentTop - Math.abs(deltaY)
					if (wouldBeTop >= minDistance) {
						newHeight -= Math.abs(deltaY)
					}
				} else if (deltaY > 0) {
					// Увеличение высоты - проверяем нижнюю границу
					const wouldBeBottom = currentBottom + Math.abs(deltaY)
					if (wouldBeBottom + minDistance <= viewportHeight) {
						newHeight += Math.abs(deltaY)
					}
				}
			}
			break

		case 'bottom-left':
		case 'bottom-right':
			if (computedPosition.value === 'left' || computedPosition.value === 'right') {
				if (deltaY > 0) {
					// Увеличение высоты - опускаем нижнюю границу
					const wouldBeBottom = currentBottom + Math.abs(deltaY)
					if (wouldBeBottom + minDistance <= viewportHeight) {
						newHeight += Math.abs(deltaY)
					}
				} else if (deltaY < 0) {
					// Уменьшение высоты - поднимаем нижнюю границу
					// Проверяем, что не достигли верхней границы
					const wouldBeTop = currentTop + Math.abs(deltaY)
					if (wouldBeTop + minDistance <= currentBottom) { // Минимальная высота
						newHeight -= Math.abs(deltaY)
					}
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaY > 0) {
					// Увеличение высоты - проверяем нижнюю границу
					const wouldBeBottom = currentBottom + Math.abs(deltaY)
					if (wouldBeBottom + minDistance <= viewportHeight) {
						newHeight += Math.abs(deltaY)
					}
				} else if (deltaY < 0) {
					// Уменьшение высоты - проверяем верхнюю границу
					const wouldBeTop = currentTop - Math.abs(deltaY)
					if (wouldBeTop >= minDistance) {
						newHeight -= Math.abs(deltaY)
					}
				}
			}
			break
	}

	switch (resizeState.direction) {
		case 'top-left':
		case 'bottom-left':
			if (computedPosition.value === 'left') {
				if (deltaX < 0) {
					const wouldBeLeft = currentLeft + deltaX
					if (wouldBeLeft >= minDistance) {
						newWidth -= deltaX
					}
				} else if (deltaX > 0) {
					newWidth -= deltaX
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaX < 0) {
					const wouldBeLeft = currentLeft + deltaX
					if (wouldBeLeft >= minDistance) {
						newWidth += deltaX
					}
				} else if (deltaX > 0) {
					const wouldBeRight = currentRight + deltaX
					if (wouldBeRight + minDistance <= viewportWidth) {
						newWidth += deltaX
					}
				}
			}
			break

		case 'top-right':
		case 'bottom-right':
			if (computedPosition.value === 'right') {
				if (deltaX > 0) {
					const wouldBeRight = currentRight + deltaX
					if (wouldBeRight + minDistance <= viewportWidth) {
						newWidth += deltaX
					}
				} else if (deltaX < 0) {
					newWidth += deltaX
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaX < 0) {
					const wouldBeLeft = currentLeft + deltaX
					if (wouldBeLeft >= minDistance) {
						newWidth += deltaX
					}
				} else if (deltaX > 0) {
					const wouldBeRight = currentRight + deltaX
					if (wouldBeRight + minDistance <= viewportWidth) {
						newWidth += deltaX
					}
				}
			}
			break
	}

	// Получаем максимальные доступные размеры
	const { maxWidth, maxHeight } = getMaxAvailableSize()
	const effectiveMaxWidth = Math.min(maxWidth, props.maxWidth)
	const effectiveMaxHeight = Math.min(maxHeight, props.maxHeight)

	// Применяем ограничения min/max
	newWidth = Math.max(props.minWidth, Math.min(newWidth, effectiveMaxWidth))
	newHeight = Math.max(props.minHeight, Math.min(newHeight, effectiveMaxHeight))

	contentWidth.value = newWidth
	contentHeight.value = newHeight
}

const stopResize = () => {
	isResizing.value = false
	clearTimers()

	if (props.name) {
		saveSizeToStorage(contentWidth.value, contentHeight.value)
	}

	resizeState = null
	document.removeEventListener('mousemove', handleResize)
	document.removeEventListener('mouseup', stopResize)
}

const loadSizeFromStorage = () => {
	if (!props.name) return null
	const data = localStorage.getItem(`dropdown-${props.name}`)
	if (!data) return null

	try {
		const { width, height } = JSON.parse(data)
		return {
			width: Math.max(props.minWidth, Math.min(width, props.maxWidth)),
			height: Math.max(props.minHeight, Math.min(height, props.maxHeight))
		}
	} catch {
		return null
	}
}

const saveSizeToStorage = (width: number, height: number) => {
	if (!props.name) return

	// Валидируем размеры перед сохранением
	const validatedSize = validateSavedSize(width, height)

	localStorage.setItem(
		`dropdown-${props.name}`,
		JSON.stringify({
			width: validatedSize.width,
			height: validatedSize.height
		})
	)
}

const checkParentScroll = () => {
	if (!wrapperRef.value || !isOpen.value || !props.hideByParentScroll) return

	const rect = wrapperRef.value.getBoundingClientRect()

	if (lastPosition.value) {
		// Уменьшаем допуск до 5px
		const positionChanged =
			Math.abs(rect.top - lastPosition.value.top) > 5 ||
			Math.abs(rect.left - lastPosition.value.left) > 5

		if (positionChanged) {
			closeDropdown()
		}
	}

	lastPosition.value = { top: rect.top, left: rect.left }
}

const updateFullscreenState = () => {
	const isMobileViewport = window.innerWidth <= 480
	isCenteredOnMobile.value = props.centeredOnMobile && isMobileViewport
	isFullscreen.value = props.fullscreenOnMobile && isMobileViewport && !isCenteredOnMobile.value
	isBottomSheetOnMobile.value = props.bottomSheetOnMobile && isMobileViewport &&
		!isCenteredOnMobile.value && !isFullscreen.value

	if (!isBottomSheetOnMobile.value) {
		stopBottomSheetHeightTracking()
		bottomSheetMinHeight.value = 0
	} else if (bottomSheetMinHeight.value) {
		bottomSheetMinHeight.value = Math.min(
			bottomSheetMinHeight.value,
			Math.max(0, window.innerHeight - 44)
		)
	}
}

onMounted(() => {
	updateFullscreenState()
	window.addEventListener('resize', updateFullscreenState)

	if (props.closeByEsc) {
		document.addEventListener('keydown', handleEsc)
	}

	if (props.hideByParentScroll) {
		const scrollObserver = () => {
			requestAnimationFrame(checkParentScroll)
		}

		const scrollableParents = findScrollableParents(wrapperRef.value)
		scrollableParents.forEach(parent => {
			parent.addEventListener('scroll', scrollObserver, { passive: true })
		})

		window.addEventListener('scroll', scrollObserver, { passive: true })

		scrollCleanups = [
			...scrollableParents.map(parent => () => parent.removeEventListener('scroll', scrollObserver)),
			() => window.removeEventListener('scroll', scrollObserver)
		]
	}
})

// Поиск всех скролящихся родителей
const findScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
	const scrollableParents: HTMLElement[] = []
	let current = element?.parentElement

	while (current) {
		const style = window.getComputedStyle(current)
		const isScrollable = style.overflow === 'auto' ||
			style.overflow === 'scroll' ||
			style.overflowY === 'auto' ||
			style.overflowY === 'scroll'

		if (isScrollable) {
			scrollableParents.push(current)
		}

		current = current.parentElement
	}

	return scrollableParents
}

onUnmounted(() => {
	window.removeEventListener('resize', updateFullscreenState)
	document.removeEventListener('keydown', handleEsc)
	document.removeEventListener('mousedown', handleClickOutside)
	document.removeEventListener('mousemove', handleResize)
	document.removeEventListener('mouseup', stopResize)
	scrollCleanups.forEach(cleanup => cleanup())
	scrollCleanups = []
	stopBottomSheetHeightTracking()
	clearTimers()
})

watch(isOpen, (newVal) => {
	if (newVal) {
		lastPosition.value = wrapperRef.value
			? wrapperRef.value.getBoundingClientRect()
			: null
	}
})

watch(() => route.fullPath, () => {
	if (isOpen.value) {
		closeDropdown()
	}
})

__expose({
	open: openDropdown,
	close: closeDropdown
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "dropdown-wrapper",
    ref_key: "wrapperRef",
    ref: wrapperRef,
    onMouseenter: handleMouseEnter,
    onMouseleave: handleMouseLeave
  }, [
    _createElementVNode("div", {
      class: "dropdown-activator",
      onClickCapture: handleActivatorClickCapture,
      onClick: _withModifiers(handleActivatorClick, ["stop"]),
      onMouseenter: handleActivatorMouseEnter,
      onMouseleave: handleActivatorMouseLeave
    }, [
      _renderSlot(_ctx.$slots, "activator")
    ], 32),
    (isOpen.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "body"
        }, [
          (isCenteredOnMobile.value || isBottomSheetOnMobile.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "dropdown-mobile-backdrop",
                style: _normalizeStyle(backdropStyles.value),
                onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                onClick: _withModifiers(handleBackdropClick, ["stop"]),
                onWheel: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
                onTouchmove: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"]))
              }, null, 36))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            ref_key: "contentRef",
            ref: contentRef,
            class: _normalizeClass(["dropdown-content", [
					`dropdown-position-${computedPosition.value}`,
					_ctx.contentClass,
					{ 'dropdown-resizable': _ctx.resizable },
					{ 'dropdown-fullscreen': isFullscreen.value },
					{ 'dropdown-centered-mobile': isCenteredOnMobile.value },
					{ 'dropdown-bottom-sheet-mobile': isBottomSheetOnMobile.value },
					{ 'dropdown-auto-size': isAutoSize.value },
					{ 'dropdown-content--hover': canUseHover.value },
					`dropdown-corners-${computedPosition.value}`
				]]),
            style: _normalizeStyle(contentStyles.value),
            onMousedown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"])),
            onMouseenter: handleContentMouseEnter,
            onMouseleave: handleContentMouseLeave
          }, [
            _createElementVNode("div", _hoisted_1, [
              (isBottomSheetOnMobile.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createElementVNode("span", null, _toDisplayString(_ctx.mobileTitle || _ctx.$t('contact_choose_action')), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "dropdown-bottom-sheet-close",
                      "aria-label": _ctx.$t('close'),
                      onClick: _withModifiers(closeDropdown, ["stop"])
                    }, [
                      _createVNode(AppIcon, {
                        name: "close",
                        width: "18px",
                        color: "#979797"
                      })
                    ], 8, _hoisted_3)
                  ]))
                : _createCommentVNode("", true),
              _renderSlot(_ctx.$slots, "default")
            ]),
            (_ctx.resizable && !isFullscreen.value && !isBottomSheetOnMobile.value)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(resizeCorners.value, (corner) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: corner.position,
                    class: _normalizeClass(["resize-handle-icon", `resize-handle-${corner.position}`]),
                    style: _normalizeStyle({ transform: `rotate(${getRotationAngle(corner.position)}deg)` }),
                    onMousedown: (e) => startResize(e, corner.direction)
                  }, [
                    _createVNode(AppIcon, {
                      name: "arrow-dropdown-resize",
                      width: "11px",
                      color: "#979797",
                      "hover-color": "#499259",
                      title: _ctx.$t('ui_resize')
                    }, null, 8, ["title"])
                  ], 46, _hoisted_4))
                }), 128))
              : _createCommentVNode("", true)
          ], 38)
        ]))
      : _createCommentVNode("", true)
  ], 544))
}
}

})